import { useState, useRef, useEffect, useMemo, useCallback } from 'react'
import {
  Animated,
  Pressable,
  StatusBar,
  Platform,
  useWindowDimensions,
  BackHandler,
  LayoutChangeEvent,
} from 'react-native'
import { Portal, View, useMedia, Spinner } from 'tamagui'
import { VideoView, useVideoPlayer } from 'expo-video'
import { activateKeepAwakeAsync, deactivateKeepAwake } from 'expo-keep-awake'
import { useEvent, useEventListener } from 'expo'
import { IconProp } from '@fortawesome/fontawesome-svg-core'
import { LinearGradient } from 'expo-linear-gradient'
import { AutoPlaySwitch } from './AutoPlaySwitch'
import { useAsyncStorage } from '@react-native-async-storage/async-storage'
import { VIDEO_AUTO_PLAY } from '@constants/storages'
import { ConfigsModal } from './ConfigsModal'
import { QualitySelector } from './QualitySelector'
import { PlaybackRateSelector } from './PlaybackRateSelector'
import { useFullScreenModeStore } from '@stores/useFullScreenModeStore'
import { useSafeAreaInsets } from 'react-native-safe-area-context'
import { useComponentTranslation } from '@hooks/useComponentTranslation'
import {
  ISparkVideosUrls,
  IUpdateLessonProgress,
} from '@components/MediaRender'
import * as NavigationBar from 'expo-navigation-bar'
import { VIDEO_WATERMARK } from '@constants/features'
import { useHasFeature } from '@hooks/useThemeParams'
import { useThemeColor } from '@hooks/useThemeColor'
import { Watermark } from './Watermark'
import { useVideoRotation } from '@components/VideoPlayer/useVideoRotation'
import { useSession } from '@hooks/useSession'
import { CenterControls } from './CenterControls'
import { BottomControls } from './BottomControls'
import { VideoThumbnail } from './VideoThumbnail'
import { VideoError } from './VideoError'
import { getVideoSurfaceSize } from '@utils/videoSurfaceSize'

export type QualityOption = {
  label: string
  value: string
  resolution: string
  bandwidth: number
}

export type PlaybackRateOption = {
  label: string
  value: number
}

export const PLAYBACK_RATE_OPTIONS: PlaybackRateOption[] = [
  { label: '0.25', value: 0.25 },
  { label: '1.0', value: 1 },
  { label: '1.25', value: 1.25 },
  { label: '1.5', value: 1.5 },
  { label: '2.0', value: 2 },
]

const IS_ANDROID = Platform.OS === 'android'
const IS_IOS = Platform.OS === 'ios'

const PROGRESS_THRESHOLDS = [25, 50, 75, 100] as const

interface IVideoPlayerProps {
  videoUrl: ISparkVideosUrls
  mediaUrl: string
  title: string
  videoContainerHeight?: number
  qualityOptions: QualityOption[]
  thumbnailUrl?: string
  isVertical: boolean
  updateProgress: (params: IUpdateLessonProgress) => void
  actualProgress?: number
  videoStartTime?: number
}

export const VideoPlayer: React.FC<IVideoPlayerProps> = ({
  videoUrl,
  title,
  videoContainerHeight = 240,
  qualityOptions,
  thumbnailUrl,
  isVertical,
  updateProgress,
  actualProgress = 0,
  videoStartTime,
}) => {
  const [showQualitySelector, setShowQualitySelector] = useState(false)
  const [currentQuality, setCurrentQuality] = useState<QualityOption>(
    qualityOptions[0],
  )
  const [isLoading, setIsLoading] = useState(false)
  const [hasStartedPlaying, setHasStartedPlaying] = useState(false)
  const [isAutoPlayActive, setIsAutoPlayActive] = useState(false)
  const [showControls, setShowControls] = useState(true)
  const [showVolumeSelector, setShowVolumeSelector] = useState(false)
  const [showPlaybackRateSelector, setShowPlaybackRateSelector] =
    useState(false)
  const [volume, setVolume] = useState(50)
  const [playbackRate, setPlaybackRate] = useState(1)
  const [showConfigsModal, setShowConfigsModal] = useState(false)
  const [timeline, setTimeline] = useState({
    currentTime: 0,
    duration: 0,
    bufferedPosition: 0,
  })
  const [videoContainerSize, setVideoContainerSize] = useState({
    width: 0,
    height: 0,
  })
  const [intrinsicVideoSize, setIntrinsicVideoSize] = useState({
    width: videoUrl?.width ?? 0,
    height: videoUrl?.height ?? 0,
  })
  const fadeAnim = useRef(new Animated.Value(1)).current
  const controlsTimeout = useRef<NodeJS.Timeout>(null)
  const previewTimeout = useRef<NodeJS.Timeout>(null)
  const videoRef = useRef<VideoView>(null)
  const videoEnded = useRef(false)
  const isMuted = useRef(false)
  const isMountedRef = useRef(true)
  const shouldRunInitialAutoPlay = useRef(false)
  const defaultQualityValueRef = useRef<string | undefined>(
    qualityOptions[0]?.value,
  )
  const currentTimeRef = useRef(0)
  const durationRef = useRef(0)
  const progressMarkersRef = useRef({
    25: actualProgress >= 25,
    50: actualProgress >= 50,
    75: actualProgress >= 75,
    100: actualProgress >= 100,
  })

  const media = useMedia()
  const { setItem, getItem, removeItem } = useAsyncStorage(VIDEO_AUTO_PLAY)
  const { setIsFullScreenMode, isFullScreenMode } = useFullScreenModeStore()
  const safeAreaInsets = useSafeAreaInsets()
  const { t } = useComponentTranslation('VideoPlayer')
  const hasFeature = useHasFeature(VIDEO_WATERMARK)
  const rangeColor = useThemeColor('accent')
  const pageFade = useThemeColor('bg-page', 0.8)
  const videoStyles = useVideoRotation({ isFullScreenMode, isVertical })
  const { height } = useWindowDimensions()
  const { user } = useSession()

  // Memoiza metadata para evitar recriações
  const videoMetadata = useMemo(
    () => ({
      artwork: thumbnailUrl || '',
      title,
    }),
    [thumbnailUrl, title],
  )

  const player = useVideoPlayer(
    {
      uri: qualityOptions[0]?.value || '',
      metadata: videoMetadata,
    },
    (playerInstance) => {
      playerInstance.timeUpdateEventInterval = 1
      playerInstance.preservesPitch = true
      playerInstance.bufferOptions = {
        preferredForwardBufferDuration: 180,
      }
    },
  )

  // No Android o player usa contentFit="fill" (HET-2339); a surface precisa ter
  // a proporção do arquivo para não esticar a imagem (HET-4382).
  useEventListener(player, 'sourceLoad', ({ availableVideoTracks }) => {
    const track = availableVideoTracks?.find(({ size }) => size.width > 0)
    if (track) {
      setIntrinsicVideoSize(track.size)
    }
  })

  useEffect(() => {
    if (videoUrl?.width > 0 && videoUrl?.height > 0) {
      setIntrinsicVideoSize({ width: videoUrl.width, height: videoUrl.height })
    }
  }, [videoUrl])

  useEffect(() => {
    const defaultQuality = qualityOptions[0]
    if (!defaultQuality) {
      return
    }

    if (defaultQualityValueRef.current === defaultQuality.value) {
      return
    }

    defaultQualityValueRef.current = defaultQuality.value
    setCurrentQuality(defaultQuality)
    player.replaceAsync({
      uri: defaultQuality.value,
      metadata: videoMetadata,
    })
  }, [player, qualityOptions, videoMetadata])

  useEffect(() => {
    if (isFullScreenMode) {
      StatusBar.setHidden(true)
      if (IS_ANDROID) {
        NavigationBar.setVisibilityAsync('hidden')
      }
    } else {
      StatusBar.setHidden(false)
      if (IS_ANDROID) {
        NavigationBar.setVisibilityAsync('visible')
      }
    }
  }, [isFullScreenMode])

  useEventListener(player, 'statusChange', (event) => {
    if (event.status === 'loading') {
      setIsLoading(true)
    } else {
      setIsLoading(false)
    }
  })

  useEventListener(player, 'playToEnd', () => {
    videoEnded.current = true

    setShowControls(true)
    Animated.timing(fadeAnim, {
      toValue: 1,
      duration: 0,
      useNativeDriver: true,
    }).start()
  })

  const { isPlaying } = useEvent(player, 'playingChange', {
    isPlaying: player.playing,
  })

  useEventListener(
    player,
    'timeUpdate',
    ({ currentTime: ct, bufferedPosition }) => {
      const duration = player.duration || durationRef.current || 0
      const nextCurrentTime = ct ?? player.currentTime ?? currentTimeRef.current
      const nextBufferedPosition =
        bufferedPosition ?? player.bufferedPosition ?? timeline.bufferedPosition

      currentTimeRef.current = nextCurrentTime
      durationRef.current = duration

      setTimeline((prev) => {
        // evita re-render desnecessário se nada mudou
        if (
          prev.currentTime === nextCurrentTime &&
          prev.duration === duration &&
          prev.bufferedPosition === nextBufferedPosition
        ) {
          return prev
        }

        return {
          currentTime: nextCurrentTime,
          duration,
          bufferedPosition: nextBufferedPosition,
        }
      })
    },
  )

  useEffect(() => {
    if (isPlaying && !hasStartedPlaying) {
      setHasStartedPlaying(true)
    }
  }, [hasStartedPlaying, isPlaying])

  useEffect(() => {
    const controlsTimeoutCurrent = controlsTimeout.current
    const previewTimeoutCurrent = previewTimeout.current

    return () => {
      isMountedRef.current = false
      if (controlsTimeoutCurrent) {
        clearTimeout(controlsTimeoutCurrent)
      }
      if (previewTimeoutCurrent) {
        clearTimeout(previewTimeoutCurrent)
      }
    }
  }, [])

  useEffect(() => {
    progressMarkersRef.current = {
      25: actualProgress >= 25,
      50: actualProgress >= 50,
      75: actualProgress >= 75,
      100: actualProgress >= 100,
    }
  }, [actualProgress])

  const fullscreenHeight = useMemo(() => {
    if (IS_IOS) {
      return height - safeAreaInsets.top - safeAreaInsets.bottom
    }
    const statusBarHeight = StatusBar.currentHeight || 0
    return height + safeAreaInsets.top + statusBarHeight + safeAreaInsets.bottom
  }, [height, safeAreaInsets.top, safeAreaInsets.bottom])

  const hasMeasuredVideoContainer =
    videoContainerSize.width > 0 && videoContainerSize.height > 0

  const videoSurfaceSize = useMemo(
    () =>
      getVideoSurfaceSize({
        containerWidth: videoContainerSize.width,
        containerHeight: videoContainerSize.height,
        videoWidth: intrinsicVideoSize.width,
        videoHeight: intrinsicVideoSize.height,
      }),
    [intrinsicVideoSize, videoContainerSize],
  )

  const handleVideoContainerLayout = useCallback((event: LayoutChangeEvent) => {
    const { width, height } = event.nativeEvent.layout
    setVideoContainerSize({ width, height })
  }, [])

  const showLoading = useMemo(() => {
    return isLoading && !hasStartedPlaying
  }, [hasStartedPlaying, isLoading])

  const bufferedPercentage = useMemo(() => {
    return Math.max(
      0,
      (timeline.bufferedPosition / Math.max(timeline.duration, 1)) * 100,
    )
  }, [timeline.bufferedPosition, timeline.duration])

  const handleProgressUpdate = useCallback(
    (threshold: number) => {
      updateProgress({
        progress: threshold,
        currentVideoTime: Math.floor(currentTimeRef.current),
      })
    },
    [updateProgress],
  )

  useEventListener(player, 'timeUpdate', ({ currentTime: ct }) => {
    if (!isPlaying) return
    const d = durationRef.current
    if (!d) return
    const progress = (ct / d) * 100

    PROGRESS_THRESHOLDS.forEach((threshold) => {
      const markers = progressMarkersRef.current
      if (!markers[threshold] && progress >= threshold) {
        progressMarkersRef.current = { ...markers, [threshold]: true }
        handleProgressUpdate(threshold)
      }
    })
  })

  // O `currentTime` do engine pode assentar logo abaixo de `duration`, então o
  // marco de 100% não trip pelo timeUpdate. O fim natural garante o report —
  // mesma proteção que o player do Moviie já tem.
  useEventListener(player, 'playToEnd', () => {
    if (progressMarkersRef.current[100]) return
    progressMarkersRef.current = { ...progressMarkersRef.current, 100: true }
    handleProgressUpdate(100)
  })

  const startControlsTimer = useCallback(() => {
    if (controlsTimeout.current) {
      clearTimeout(controlsTimeout.current)
    }

    setShowControls(true)
    Animated.timing(fadeAnim, {
      toValue: 1,
      duration: 0,
      useNativeDriver: true,
    }).start()

    controlsTimeout.current = setTimeout(() => {
      Animated.timing(fadeAnim, {
        toValue: 0,
        duration: 300,
        useNativeDriver: true,
      }).start(() => setShowControls(false))
    }, 3000)
  }, [fadeAnim])

  useEffect(() => {
    if (isPlaying && showControls) {
      startControlsTimer()
    } else if (!isPlaying) {
      if (controlsTimeout.current) {
        clearTimeout(controlsTimeout.current)
      }
      setShowControls(true)
      Animated.timing(fadeAnim, {
        toValue: 1,
        duration: 0,
        useNativeDriver: true,
      }).start()
    }
    return () => {
      if (controlsTimeout.current) {
        clearTimeout(controlsTimeout.current)
      }
    }
  }, [fadeAnim, isPlaying, showControls, startControlsTimer])

  useEffect(() => {
    let isCancelled = false

    const hydrateAutoPlayPreference = async () => {
      try {
        const storedValue = await getItem()
        if (isCancelled) {
          return
        }
        const shouldEnable = storedValue === 'true'
        setIsAutoPlayActive(shouldEnable)
        shouldRunInitialAutoPlay.current = shouldEnable
      } catch (error) {
        console.warn('Failed to read autoplay preference', error)
      }
    }

    hydrateAutoPlayPreference()

    return () => {
      isCancelled = true
    }
  }, [getItem])

  useEffect(() => {
    if (!shouldRunInitialAutoPlay.current || !isAutoPlayActive) {
      return
    }

    shouldRunInitialAutoPlay.current = false
    let autoPlayTimeout: NodeJS.Timeout | null = null

    autoPlayTimeout = setTimeout(() => {
      if (!isMountedRef.current) {
        return
      }
      player.play()
      setHasStartedPlaying(true)
    }, 1000)

    return () => {
      if (autoPlayTimeout) {
        clearTimeout(autoPlayTimeout)
      }
    }
  }, [isAutoPlayActive, player])

  useEffect(() => {
    if (isPlaying && IS_ANDROID) {
      activateKeepAwakeAsync()
    } else {
      deactivateKeepAwake()
    }

    return () => {
      deactivateKeepAwake()
    }
  }, [isPlaying])

  const handleQualityChange = useCallback(
    async (newQuality: QualityOption) => {
      if (newQuality.value === currentQuality.value) {
        return
      }
      try {
        setIsLoading(true)
        const timeToResume = currentTimeRef.current
        const wasPlaying = isPlaying

        await player.replaceAsync({
          uri: newQuality.value,
          metadata: videoMetadata,
        })

        player.currentTime = timeToResume

        if (wasPlaying) {
          player.play()
        }

        setCurrentQuality({ ...newQuality })
      } catch (error) {
        throw new Error(t('error.qualityChange', { error }))
      } finally {
        setIsLoading(false)
      }
    },
    [currentQuality.value, player, isPlaying, videoMetadata, t],
  )

  const resetProgressMarkers = useCallback((newTime: number) => {
    const d = durationRef.current
    if (!d) return

    const progress = (newTime / d) * 100
    const currentMarkers = progressMarkersRef.current
    const updatedMarkers = { ...currentMarkers }

    PROGRESS_THRESHOLDS.forEach((threshold) => {
      if (progress < threshold) {
        updatedMarkers[threshold] = false
      }
    })

    progressMarkersRef.current = updatedMarkers
  }, [])

  const handleProgressChange = useCallback(
    (value: number) => {
      const d = durationRef.current
      const newTime = (value / 100) * d

      if (previewTimeout.current) {
        clearTimeout(previewTimeout.current)
      }

      resetProgressMarkers(newTime)

      currentTimeRef.current = newTime
      setTimeline((prev) => ({
        ...prev,
        currentTime: newTime,
      }))
      player.currentTime = newTime
    },
    [player, resetProgressMarkers],
  )

  const handlePlayPause = useCallback(() => {
    if (isPlaying) {
      player.pause()
      if (controlsTimeout.current) {
        clearTimeout(controlsTimeout.current)
      }
      setShowControls(true)
      Animated.timing(fadeAnim, {
        toValue: 1,
        duration: 0,
        useNativeDriver: true,
      }).start()
    } else if (videoEnded.current) {
      player.currentTime = 0
      player.play()
      videoEnded.current = false
      startControlsTimer()
    } else {
      if (videoStartTime && !hasStartedPlaying) {
        player.currentTime = videoStartTime
      }
      setHasStartedPlaying(true)
      player.play()
    }
  }, [
    isPlaying,
    player,
    fadeAnim,
    videoStartTime,
    hasStartedPlaying,
    startControlsTimer,
  ])

  const handleSkip = useCallback(
    (seconds: number) => {
      const ct = currentTimeRef.current
      const d = durationRef.current
      const newTime = Math.min(Math.max(ct + seconds, 0), d)

      if (seconds < 0) {
        resetProgressMarkers(newTime)
      }

      currentTimeRef.current = newTime
      setTimeline((prev) => ({
        ...prev,
        currentTime: newTime,
      }))
      player.currentTime = newTime
    },
    [player, resetProgressMarkers],
  )

  const handlePlaybackRateChange = useCallback(
    (rate: number) => {
      setPlaybackRate(rate)
      player.playbackRate = rate
    },
    [player],
  )

  const refreshVideoSurface = useCallback(async () => {
    if (!IS_ANDROID) return

    const savedTime = player.currentTime
    const wasPlaying = player.playing
    const savedPlaybackRate = player.playbackRate

    try {
      // Recarrega o vídeo com a mesma URL para forçar reconexão da surface
      await player.replaceAsync({
        uri: currentQuality.value,
        metadata: videoMetadata,
      })

      // Restaura o estado anterior
      player.currentTime = savedTime
      player.playbackRate = savedPlaybackRate

      // Retoma a reprodução se estava tocando
      if (wasPlaying) {
        player.play()
      }
    } catch (error) {
      console.warn('Failed to refresh video surface:', error)
    }
  }, [player, currentQuality.value, videoMetadata])

  const handleFullScreen = useCallback(() => {
    if (!isMountedRef.current) return
    setIsFullScreenMode(!isFullScreenMode)
  }, [setIsFullScreenMode, isFullScreenMode])

  // Refresh da surface quando entra/sai do fullscreen (apenas Android)
  useEffect(() => {
    if (!IS_ANDROID) return
    if (!hasStartedPlaying) return

    // Delay para garantir que o Portal foi renderizado
    const timeoutId = setTimeout(() => {
      refreshVideoSurface()
    }, 100)

    return () => clearTimeout(timeoutId)
  }, [isFullScreenMode, hasStartedPlaying, refreshVideoSurface])

  useEffect(() => {
    if (!IS_ANDROID || !isFullScreenMode) return

    const backHandler = BackHandler.addEventListener(
      'hardwareBackPress',
      () => {
        setIsFullScreenMode(false)
        return true
      },
    )

    return () => backHandler.remove()
  }, [isFullScreenMode, setIsFullScreenMode])

  const toggleMute = useCallback(() => {
    if (media.gtMd) {
      setShowVolumeSelector(!showVolumeSelector)
    } else {
      player.muted = !isMuted.current
      isMuted.current = !isMuted.current
    }
  }, [isMuted, media.gtMd, player, showVolumeSelector])

  const handleVolumeValueChange = useCallback(
    (value: number) => {
      player.volume = value / 100
      setVolume(value)
    },
    [player],
  )

  const errorHasOccurred = useMemo(() => {
    return player.status === 'error'
  }, [player.status])

  const handlePortalZIndex = useCallback(() => {
    if (IS_IOS) {
      return 1
    }
    return showConfigsModal || showQualitySelector || showPlaybackRateSelector
      ? 1
      : 99999999
  }, [showConfigsModal, showQualitySelector, showPlaybackRateSelector])

  const handlePictureInPicture = useCallback(async () => {
    try {
      if (videoRef.current) {
        await videoRef.current.startPictureInPicture()
      }
    } catch (error) {
      throw new Error(t('error.pictureInPicture', { error }))
    }
  }, [videoRef, t])

  const handleAutoPlay = useCallback(() => {
    setIsAutoPlayActive((prev: boolean) => {
      const nextValue = !prev

      if (nextValue) {
        setItem('true')
      } else {
        removeItem()
      }

      return nextValue
    })
  }, [removeItem, setItem])

  const autoPlayIcon: IconProp = useMemo(
    () => (isAutoPlayActive ? ['fas', 'play'] : ['fas', 'pause']),
    [isAutoPlayActive],
  )

  const iconSizes = useMemo(
    () => ({
      bottomControls: 20,
      playPauseCenter: media.gtMd ? 64 : 32,
      advanceBackward: 32,
    }),
    [media.gtMd],
  )

  const shouldMuteIcon = useMemo(
    () => isMuted.current || volume === 0,
    [isMuted, volume],
  )
  const volumeIcon: IconProp = useMemo(
    () => (shouldMuteIcon ? ['fal', 'volume-mute'] : ['fal', 'volume-high']),
    [shouldMuteIcon],
  )

  const controlBottomPosition = useMemo(() => {
    return isFullScreenMode ? safeAreaInsets.bottom : 0
  }, [isFullScreenMode, safeAreaInsets.bottom])

  const currentTimePercentage = useMemo(() => {
    return Math.max(
      0,
      (timeline.currentTime / Math.max(timeline.duration, 1)) * 100,
    )
  }, [timeline.currentTime, timeline.duration])

  const shouldShowWatermark = useMemo(
    () => hasFeature && hasStartedPlaying,
    [hasFeature, hasStartedPlaying],
  )

  const watermarkText = useMemo(
    () => user?.cpf || `${user?.firstName} ${user?.lastName || ''}`,
    [user?.cpf, user?.firstName, user?.lastName],
  )

  const handleSkipBackward = useCallback(() => handleSkip(-10), [handleSkip])
  const handleSkipForward = useCallback(() => handleSkip(10), [handleSkip])

  const handleOpenConfigs = useCallback(() => {
    setShowConfigsModal((prev) => !prev)
    setShowQualitySelector(false)
    setShowPlaybackRateSelector(false)
  }, [])

  const renderConfigsModal = useMemo(() => {
    if (!showConfigsModal) return null

    return (
      <ConfigsModal
        open={showConfigsModal}
        onClose={() => setShowConfigsModal(false)}
        openQualitySelector={() => setShowQualitySelector(true)}
        openSpeedSelector={() => setShowPlaybackRateSelector(true)}
        values={{
          quality: currentQuality.label,
          speed: playbackRate.toString(),
        }}
      />
    )
  }, [currentQuality.label, playbackRate, showConfigsModal])

  const renderQualitySelector = useMemo(() => {
    if (!showQualitySelector) return null

    return (
      <QualitySelector
        open={showQualitySelector}
        onClose={() => setShowQualitySelector(false)}
        qualityOptions={qualityOptions}
        onQualityChange={handleQualityChange}
        currentQuality={currentQuality}
      />
    )
  }, [currentQuality, handleQualityChange, qualityOptions, showQualitySelector])

  const renderPlaybackRateSelector = useMemo(() => {
    if (!showPlaybackRateSelector) return null

    return (
      <PlaybackRateSelector
        open={showPlaybackRateSelector}
        onClose={() => setShowPlaybackRateSelector(false)}
        playbackRateOptions={PLAYBACK_RATE_OPTIONS}
        onPlaybackRateChange={handlePlaybackRateChange}
        currentPlaybackRate={playbackRate}
      />
    )
  }, [handlePlaybackRateChange, playbackRate, showPlaybackRateSelector])

  const shouldShowThumbnail = useMemo(
    () =>
      !hasStartedPlaying &&
      !isLoading &&
      !errorHasOccurred &&
      !isFullScreenMode,
    [hasStartedPlaying, isLoading, errorHasOccurred, isFullScreenMode],
  )

  // Estilos memoizados para evitar recriações
  const containerStyle = useMemo(
    () => [
      {
        flex: 1,
        position: 'relative' as const,
        zIndex: 1,
        height: '100%' as const,
      },
      isFullScreenMode && !isVertical ? videoStyles : null,
    ],
    [isFullScreenMode, isVertical, videoStyles],
  )

  const controlsOverlayStyle = useMemo(() => {
    const width =
      isFullScreenMode && !isVertical ? Number(fullscreenHeight) : null
    return {
      position: 'absolute' as const,
      top: 0,
      left: 0,
      right: 0,
      bottom: 0,
      opacity: fadeAnim,
      zIndex: 2,
      width,
    }
  }, [fadeAnim, fullscreenHeight, isFullScreenMode, isVertical])

  const handleShowControls = useCallback(() => {
    startControlsTimer()
  }, [startControlsTimer])

  const pressableOverlayStyle = useMemo(
    () => ({
      position: 'absolute' as const,
      height: '100%' as const,
      width: '100%' as const,
      zIndex: 1,
    }),
    [],
  )

  const videoContainerStyle = useMemo(
    () => ({
      flex: 1,
      position: 'relative' as const,
      alignItems: 'center' as const,
      justifyContent: 'center' as const,
    }),
    [],
  )

  const videoViewStyle = useMemo(
    () =>
      hasMeasuredVideoContainer
        ? {
            width: videoSurfaceSize.width,
            height: videoSurfaceSize.height,
          }
        : {
            flex: 1,
            width: '100%' as const,
            height: '100%' as const,
          },
    [hasMeasuredVideoContainer, videoSurfaceSize],
  )

  const gradientStyle = useMemo(
    () => ({
      width: '100%' as const,
      height: '100%' as const,
      position: 'absolute' as const,
      bottom: 0,
      left: 0,
      right: 0,
    }),
    [],
  )

  const videoPlayerContent = (
    <View
      height={isFullScreenMode ? fullscreenHeight : videoContainerHeight}
      width="100%"
      backgroundColor="$bg-chrome"
      overflow="visible"
      position="relative"
      top={0}
      left={0}
      right={0}
      bottom={0}
    >
      <View style={containerStyle}>
        {shouldShowThumbnail && (
          <VideoThumbnail
            thumbnailUrl={thumbnailUrl}
            title={title}
            onPlayPress={handlePlayPause}
            playButtonSize={iconSizes.playPauseCenter}
            playPauseLabel={t('playPause')}
          />
        )}
        <Pressable
          accessible
          accessibilityRole="button"
          accessibilityLabel={t('showControlsButtonLabel')}
          accessibilityHint={t('showControlsButtonHint')}
          style={pressableOverlayStyle}
          onPress={handleShowControls}
        />
        <View style={videoContainerStyle} onLayout={handleVideoContainerLayout}>
          <VideoView
            ref={videoRef}
            player={player}
            style={videoViewStyle}
            nativeControls={false}
            contentFit={IS_ANDROID ? 'fill' : 'contain'}
            allowsFullscreen={!!media.gtMd}
            allowsPictureInPicture
            surfaceType={IS_ANDROID ? 'textureView' : undefined}
          />
        </View>
        {errorHasOccurred && (
          <VideoError
            title={t('error.playback.title')}
            message={t('error.playback.message')}
          />
        )}
        {shouldShowWatermark && (
          <Watermark
            isPlaying={isPlaying}
            isFullScreenMode={isFullScreenMode}
            watermarkText={watermarkText}
          />
        )}
        {!showLoading && (
          <Animated.View
            style={controlsOverlayStyle}
            pointerEvents={showControls ? 'auto' : 'none'}
          >
            <LinearGradient
              colors={['transparent', pageFade]}
              style={gradientStyle}
            />
            <CenterControls
              onSkipBackward={handleSkipBackward}
              onSkipForward={handleSkipForward}
              onPlayPause={handlePlayPause}
              isPlaying={isPlaying}
              advanceBackwardSize={iconSizes.advanceBackward}
              playPauseCenterSize={iconSizes.playPauseCenter}
              backLabel={t('back10s')}
              skipLabel={t('skip10s')}
              playPauseLabel={t('playPause')}
            />
            <BottomControls
              controlBottomPosition={controlBottomPosition}
              isDesktop={!!media.gtMd}
              isMobile={!!media.md}
              onPlayPause={handlePlayPause}
              isPlaying={isPlaying}
              bottomControlsSize={iconSizes.bottomControls}
              onToggleMute={toggleMute}
              volumeIcon={volumeIcon}
              showVolumeSelector={showVolumeSelector}
              volume={volume}
              onVolumeChange={handleVolumeValueChange}
              currentTime={timeline.currentTime}
              isAutoPlayActive={isAutoPlayActive}
              autoPlayIcon={autoPlayIcon}
              onAutoPlay={handleAutoPlay}
              onOpenConfigs={handleOpenConfigs}
              onPictureInPicture={handlePictureInPicture}
              onFullScreen={handleFullScreen}
              isFullScreenMode={isFullScreenMode}
              rangeColor={rangeColor}
              currentTimePercentage={currentTimePercentage}
              onProgressChange={handleProgressChange}
              bufferedPercentage={bufferedPercentage}
              labels={{
                playPause: t('playPause'),
                mute: t('mute'),
                openQualityMenu: t('openQualityMenu'),
                pictureInPicture: t('pictureInPicture'),
                fullscreen: t('fullscreen'),
              }}
            />
            {media.md && !isFullScreenMode && (
              <View position="absolute" top={8} right={16} zIndex={10}>
                <AutoPlaySwitch
                  isActive={isAutoPlayActive}
                  icon={autoPlayIcon}
                  onPress={handleAutoPlay}
                />
              </View>
            )}
          </Animated.View>
        )}
        {showLoading && (
          <View
            position="absolute"
            top={0}
            left={0}
            right={0}
            bottom={0}
            backgroundColor="rgba(0, 0, 0, 0.5)"
            justifyContent="center"
            alignItems="center"
            zIndex={1}
          >
            <Spinner size="large" color="$text-on-media" />
          </View>
        )}
        {renderConfigsModal}
        {renderQualitySelector}
        {renderPlaybackRateSelector}
      </View>
    </View>
  )

  if (isFullScreenMode) {
    return (
      <Portal zIndex={handlePortalZIndex()}>
        <View
          position="absolute"
          top={0}
          left={0}
          right={0}
          bottom={0}
          backgroundColor="$bg-chrome"
          alignItems="center"
          justifyContent="center"
        >
          {videoPlayerContent}
        </View>
      </Portal>
    )
  }

  return videoPlayerContent
}
